/**
 * `<OutlineRow/>` — one row of the document-style outline (B2.3 / B2.4-left).
 *
 * Issue #759 (Phase 2, PR-2). A row is: a status dot, the title (a static
 * span until the row is being edited, then an inline `<input>`), indented by
 * depth, with assignee avatars + a note count on the right — the exact
 * anatomy of every row in `B2-3-outline-editor.svg`.
 *
 * The row is presentational: every gesture is a callback the `<OutlineEditor/>`
 * wires to a mutation. The editor owns the keyboard model (Enter/Tab/…); the
 * row just renders the right affordance for its edit state and forwards the
 * raw key event up. Selection + the inline-edit cursor are shared UI state
 * (`treeStore`), so the same row highlights in both panes of the split view.
 *
 * Brand (§A9): the avatars + note count are "who's on this / has a note",
 * never a score about a person. Calm, factual.
 *
 * Editorial revamp (#1043, Wave 1): theme-aware. EDITORIAL (flag on) is the
 * wireframe `.t-outline .row` (`tarii.css`): 13px / 1.7 line-height sans, an
 * `#FFF6D6` highlight painted on the *title* of the active row (not a full-row
 * fill), and — for the editing row — an ink left-border + a blinking caret on
 * the input (`.row.editing .name`). Assignees use the theme-aware `<AvatarRow/>`
 * idiom (saturated fill, white initials). LEGACY (flag off) renders
 * byte-identical to today (full-row soft-mint fill, indigo selected text).
 */

import { type KeyboardEvent, useEffect, useRef } from "react";

import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import { useTeamZone } from "../theme/TeamZoneContext";
import type { TreeNode } from "../tree/buildTree";
import { PALETTE } from "../tree/palette";
import { StatusDot } from "./StatusChip";
import { timingMeta } from "./taskTimingMeta";

/** Per-depth indent, px (mirrors the rail's `8 + depth*14`). */
const INDENT_PX = 22;
const BASE_PAD = 8;

/** The editorial active-row title highlight (`tarii.css` `.t-outline .row.active .name`). */
const EDITORIAL_ACTIVE_BG = "#FFF6D6";
/** Legacy selected-title ink (the gallery indigo). */
const LEGACY_SELECTED_INK = "#4F46E5";

export interface OutlineRowProps {
  node: TreeNode;
  depth: number;
  members: TaskMember[];
  /** Whether this row is the focused/selected node (highlights in both panes). */
  selected: boolean;
  /** Whether this row's title is being edited inline (the cursor is here). */
  editing: boolean;
  /** Current draft text while editing (controlled by the editor). */
  draft: string;
  /**
   * Single-click: open the node's detail drawer (also selects it). This is the
   * "expanded view" the founder chose for issue #960. The drawer is the full
   * detail surface — the outline row is the compact surface.
   */
  onOpenNode: () => void;
  /**
   * Double-click: begin inline-editing this row's title. Replaces the old
   * single-click-on-selected behaviour (#960: rename moves to double-click so
   * single-click can open the drawer without first requiring a selection step).
   */
  onBeginEdit: () => void;
  /** Draft text changed. */
  onDraftChange: (value: string) => void;
  /** A key was pressed in the title input — the editor interprets it. */
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** The input lost focus — the editor commits the draft. */
  onBlur: () => void;
  /** Placeholder shown in the active edit input (contextual, per the SVG). */
  placeholder?: string | undefined;
  /**
   * Whether this node has children — drives the collapse chevron (#1043). A
   * row with no children renders a spacer in the chevron slot so titles stay
   * aligned at the same depth.
   */
  hasChildren?: boolean;
  /** Whether this node's subtree is collapsed (the shared `treeStore.collapsed`). */
  collapsed?: boolean;
  /** Direct-child count, shown beside a collapsed chevron (optional nicety). */
  childCount?: number;
  /**
   * Toggle this node's collapsed state — the SAME `treeStore.toggleCollapse`
   * the canvas uses, so collapsing here folds the subtree on the canvas too
   * (one source of truth, B8.3). Omitted ⇒ no chevron (legacy-by-omission).
   */
  onToggleCollapse?: (() => void) | undefined;
}

export function OutlineRow({
  node,
  depth,
  members,
  selected,
  editing,
  draft,
  onOpenNode,
  onBeginEdit,
  onDraftChange,
  onKeyDown,
  onBlur,
  placeholder,
  hasChildren = false,
  collapsed = false,
  childCount = 0,
  onToggleCollapse,
}: OutlineRowProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const teamZone = useTeamZone();
  const timingText = timingMeta(node, teamZone);
  const inputRef = useRef<HTMLInputElement>(null);

  // When this row enters edit mode, focus the input + place the cursor at the
  // end (native feel — no jumpy caret, per §A7 "beautiful is acceptance").
  useEffect(() => {
    if (editing) {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }
  }, [editing]);

  const assignees = node.assigneeIds
    .map((id) => members.find((m) => m.membershipId === id))
    .filter((m): m is TaskMember => Boolean(m));

  const isRoot = depth === 0;

  // Title ink (shared by the static span + the edit input). Editorial: active
  // rows keep the ink colour (the `#FFF6D6` chip carries selection, not the
  // text colour); a planned non-root row is muted. Legacy: indigo when selected.
  const titleInk = editorial
    ? node.state === "planned" && depth > 0 && !selected
      ? c.muted
      : c.ink
    : selected
      ? LEGACY_SELECTED_INK
      : node.state === "planned" && depth > 0
        ? PALETTE.muted
        : PALETTE.ink;
  // Editorial weight: root weight-500 (`.row .name.head`), else 400. Legacy: root 600.
  const titleWeight = isRoot ? (editorial ? 500 : 600) : 400;

  return (
    <div
      data-testid="outline-row"
      data-node-id={node.id}
      data-depth={depth}
      data-selected={selected}
      className="group flex items-center gap-2 rounded-md py-1 pr-3 transition-colors"
      style={{
        paddingLeft: BASE_PAD + depth * INDENT_PX,
        // Editorial paints the highlight on the title, not the whole row
        // (`.t-outline .row.active .name`); legacy keeps the full-row mint fill.
        backgroundColor: editorial ? "transparent" : selected ? PALETTE.rollupBg : "transparent",
        fontFamily: editorial ? theme.fonts.sans : undefined,
        fontSize: editorial ? "13px" : undefined,
        lineHeight: editorial ? 1.7 : undefined,
      }}
    >
      {/* Collapse chevron (#1043) — only when this node has children AND a
          toggle handler is wired. It flips the SHARED `treeStore.collapsed`
          set, so folding here also folds the same subtree on the canvas (B8.3,
          one source of truth). A childless row (or no handler) renders a spacer
          so titles stay aligned at the same depth. ▸ collapsed / ▾ expanded. */}
      {onToggleCollapse && hasChildren ? (
        <button
          type="button"
          data-testid="outline-chevron"
          aria-label={collapsed ? "Expand subtree" : "Collapse subtree"}
          aria-expanded={!collapsed}
          title={
            collapsed
              ? `Expand · ${childCount} ${childCount === 1 ? "subtask" : "subtasks"}`
              : "Collapse"
          }
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse();
          }}
          className="-ml-1 flex h-4 w-4 shrink-0 items-center justify-center rounded text-[10px] leading-none transition-colors hover:opacity-70"
          style={{ color: editorial ? c.muted : PALETTE.muted }}
        >
          {collapsed ? "▸" : "▾"}
        </button>
      ) : (
        <span aria-hidden className="-ml-1 h-4 w-4 shrink-0" data-testid="outline-chevron-spacer" />
      )}

      <span className="shrink-0">
        <StatusDot state={node.state} />
      </span>

      {/* Title — static span until this row is the edit cursor. */}
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          data-testid="outline-input"
          // Editorial editing row: an ink left-border + a `#FFF` ground
          // (`.t-outline .row.editing .name`); the blinking caret is the native
          // input cursor (a faithful caret without a synthetic element).
          className={`min-w-0 flex-1 bg-transparent px-1 py-0.5 text-[13px] outline-none ${
            editorial ? "rounded-none" : "rounded border-none"
          }`}
          style={{
            color: titleInk,
            fontWeight: titleWeight,
            fontFamily: editorial ? theme.fonts.sans : undefined,
            backgroundColor: editorial ? "#FFFFFF" : undefined,
            borderLeft: editorial ? `2px solid ${c.ink}` : undefined,
            paddingLeft: editorial ? 6 : undefined,
            caretColor: editorial ? c.ink : undefined,
          }}
        />
      ) : (
        <button
          type="button"
          data-testid="outline-title"
          onClick={() => onOpenNode()}
          onDoubleClick={() => onBeginEdit()}
          className={`min-w-0 flex-1 truncate text-left text-[13px] ${
            editorial ? "px-1 py-0.5" : "px-1 py-0.5"
          }`}
          style={{
            color: titleInk,
            fontWeight: titleWeight,
            fontFamily: editorial ? theme.fonts.sans : undefined,
            // Editorial selection = the `#FFF6D6` chip painted on the title.
            backgroundColor: editorial && selected ? EDITORIAL_ACTIVE_BG : undefined,
            borderRadius: editorial && selected ? 3 : undefined,
          }}
        >
          {node.title}
        </button>
      )}

      {timingText ? (
        <span
          data-testid="outline-timing"
          className="max-w-[40%] shrink-0 truncate text-[10px] font-semibold uppercase"
          style={{
            color: editorial ? c.muted : PALETTE.muted,
            fontFamily: theme.fonts.mono,
            letterSpacing: "0.06em",
          }}
        >
          {timingText}
        </span>
      ) : null}

      {/* Right column: collapsed-subtask count, note count, then avatars. */}
      <span className="ml-auto flex shrink-0 items-center gap-2">
        {/* A collapsed parent surfaces how many subtasks are hidden — a calm,
            factual count about the work (never a person, §A6). */}
        {collapsed && hasChildren && childCount > 0 && (
          <span
            data-testid="outline-collapsed-count"
            title={`${childCount} ${childCount === 1 ? "subtask" : "subtasks"} hidden`}
            className="text-[10.5px] leading-none"
            style={{ color: editorial ? c.faint : PALETTE.faint }}
          >
            ▸ {childCount}
          </span>
        )}
        {node.description && (
          <span
            data-testid="outline-note-glyph"
            title="Has a note"
            aria-label="Has a note"
            className="text-[11px] leading-none"
            style={{ color: editorial ? c.muted : PALETTE.muted }}
          >
            💬
          </span>
        )}
        {assignees.map((m, i) => (
          <span
            key={m.membershipId}
            title={m.name}
            data-testid="outline-avatar"
            className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold ${
              editorial ? "font-semibold" : ""
            }`}
            style={{
              // Editorial: saturated fill, white initials, cream ring (the
              // `.t-av` idiom shared with `<AvatarRow/>`); legacy: alternating
              // mint fills + dark-ink initials (byte-identical to today).
              backgroundColor: editorial
                ? editorialAvatarTone(m.initials)
                : i % 2 === 0
                  ? PALETTE.avatarA
                  : PALETTE.avatarB,
              color: editorial ? "#FFFFFF" : PALETTE.ink,
              border: editorial ? `1.5px solid ${c.cardBg}` : undefined,
              fontFamily: editorial ? theme.fonts.sans : undefined,
            }}
          >
            {m.initials}
          </span>
        ))}
      </span>
    </div>
  );
}

/**
 * The editorial avatar tones (`tarii.css` `--av-*`): cobalt · sage · amber ·
 * purple · terracotta. Picked deterministically from a member's initials so
 * the same person is the same colour across every surface (mirrors
 * `Avatar.tsx`'s `editorialTone` — one node reads identically everywhere).
 */
const EDITORIAL_AVATAR_TONES = ["#4566A0", "#5E8E5C", "#C9852D", "#8C5BA1", "#B0593D"] as const;
function editorialAvatarTone(initials: string): string {
  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = (hash * 31 + initials.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % EDITORIAL_AVATAR_TONES.length;
  return EDITORIAL_AVATAR_TONES[idx] as string;
}
